import {
  ChangeDetectionStrategy,
  Component,
  Input,
  ChangeDetectorRef,
  Output,
  EventEmitter,
  OnInit,
  ViewChild,
} from "@angular/core";
import { CalendarItem } from "./calendar-item";
import { CalendarView } from "./calendar-view";
import { CalendarComponent } from "./calendar.component";
import { MediaRepository } from "../../domain/media-repository";
import { CalendarSearchService } from "./services/calendar-search.service";
import { Subscription } from "rxjs";
import { LiveShow, MediaShow, Show } from "../../domain/show";
import { ConversionUtils } from "../../domain/conversion-utils";
import { SecEventType } from "../../domain/sec-events";
import { PopOverComponent } from "../pop-over/pop-over.component";
import moment from "moment";

const smallVideoWidth = 260;
const midVideoHeight = 56;
const smallVideoHeight = 22;

@Component({
  selector: "icn-calendar-video",
  templateUrl: "./calendar-video.component.html",
  styleUrls: ["./calendar-video.component.scss"],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class CalendarVideoComponent implements OnInit {
  @Input() currentView: CalendarView;
  @Input() video: CalendarItem;
  @Input() index: number;
  @Input() channelFramerate: number;

  @Output() dragStart = new EventEmitter<CalendarItem>();
  @Output() dragEnd = new EventEmitter<CalendarItem>();
  @Output() durationAdjustDragStart = new EventEmitter<Show>();
  @Output() durationAdjustDragEnd = new EventEmitter<Show>();
  @Output()
  onClickVideoSubtitles: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild("popup") popup: PopOverComponent;

  subtitlesPopoverVisible: boolean = false;
  highlightSearchResult: boolean = false;
  searchTerm: string;
  searchSubscription: Subscription;
  foundedSubItem: Show = null;
  originalHeight = 0;
  secEventType = SecEventType;
  popupHiddenOnMouseLeave: boolean = true;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private calendarSearchService: CalendarSearchService,
    private calendarComponent: CalendarComponent,
  ) {}

  onDurationAdjusterDragStart(e: DragEvent) {
    e.stopPropagation();

    if (this.video.items.length > 1) {
      e.preventDefault();
      return;
    }

    e.dataTransfer.setData(
      "text/durationadjust",
      JSON.stringify(this.video.items[0]),
    );
    this.durationAdjustDragStart.emit(this.video.items[0]);
  }

  isShowContract(video: CalendarItem) {
    if (video.mediaId === "" && (video.feedId !== "" || video.promoId !== "")) {
      return false;
    }
    let show = <MediaShow>video.items[0];
    return show.isContract;
  }

  onDurationAdjusterDragEnd(e: DragEvent) {
    e.stopPropagation();
    this.durationAdjustDragEnd.emit(this.video.items[0]);
  }

  ngOnInit() {
    this.originalHeight = this.video.height;
    this.searchSubscription =
      this.calendarSearchService.highlightUpdate.subscribe(
        this.onHighlightUpdated.bind(this),
      );
  }

  onHighlightUpdated(itemId: string) {
    this.highlightSearchResult = this.video.items.some(
      (item) => itemId == item.id,
    );
    this.changeDetectorRef.markForCheck();
  }

  /**
   * So-long marianne
   * @return void
   */
  ngOnDestroy() {}

  /**
   * perform regular expression search term
   * @param term
   * @return {boolean}
   */
  testSearchTermMatch(term: string, haystack: string): boolean {
    let regEx: RegExp = new RegExp(`(${term})`, "ig");
    return regEx.test(haystack);
  }

  isSmallWidth(): boolean {
    return this.currentView.daysCount > 1;
  }

  isMediumHeight(): boolean {
    return (
      this.video.height <= midVideoHeight &&
      this.video.height > smallVideoHeight
    );
  }

  isSmallHeight(): boolean {
    return this.video.height <= smallVideoHeight;
  }

  isDraggable(video) {
    if (video.items.length > 1) {
      return null;
    }
    return "true";
  }

  isMultipleItems() {
    return this.video.items.length > 1;
  }

  formatVideoHours() {
    return (
      this.formatDuration(moment(this.video.items[0].startTime.valueOf())) +
      " - " +
      this.formatDuration(
        moment(this.video.items[this.video.items.length - 1].endTime.valueOf()),
      )
    );
  }

  formatName() {
    if (this.video.items.length > 1) {
      return "Multiple Items";
    }
    let name = this.calendarSearchService.highlightTerm(
      this.video.items[0].name,
      this.video.items[0].id,
    );
    return `${name} (som: ${this.formatDuration(
      moment.utc(this.video.items[0].som.asMilliseconds()),
    )}, duration: ${this.formatDuration(
      moment.utc(this.video.items[0].duration.asMilliseconds()),
    )})`;
  }

  formatDuration(duration: moment.Moment) {
    if (!this.channelFramerate) {
      return duration.format("HH:mm:ss.SSS");
    }

    let fps = this.padFps(
      ConversionUtils.getFpsFromMs(
        duration.milliseconds(),
        this.channelFramerate,
      ),
    );
    return duration.format("HH:mm:ss") + fps;
  }

  padFps(fps: number) {
    return fps < 10 ? ".0" + fps : "." + fps;
  }

  onDragStart(event: DragEvent) {
    event.dataTransfer.dropEffect = "move";
    event.dataTransfer.setData(
      "text/show",
      JSON.stringify(this.video.items[0]),
    );

    setTimeout(() => {
      this.video.isDragged = true;
      this.dragStart.emit(this.video);
    });
  }

  onDragEnd() {
    this.video.isDragged = false;
    this.dragEnd.emit(this.video);
  }

  formatSubtitlesCount() {
    if (this.video.subtitles.length === 1) {
      return this.video.subtitles[0].language;
    }
    return this.video.subtitles.length;
  }

  onSubtitlesClick(e: Event, _subtitlesPopover?: unknown): void {
    let videoClicked = this.video;
    this.onClickVideoSubtitles.emit({ e, videoClicked });
    e.stopImmediatePropagation();
  }

  showDurationAdjuster() {
    return (
      this.video.items.length === 1 && this.video.items[0] instanceof LiveShow
    );
  }

  secEventTypeExists(secEvents, secEventType: SecEventType) {
    return secEvents.some((event) => event.type === secEventType);
  }

  viewOptionChangeEvent(viewOption, id) {
    this.calendarComponent.setViewOptionSecEventPopup(viewOption, id);
    this.popup.hide();
  }

  showSecEventsPopupOnClick(event: MouseEvent): void {
    this.popup.show(event);
    this.popupHiddenOnMouseLeave = false;
  }

  showSecEventsPopupOnMouseEnter(event: MouseEvent): void {
    if (this.popupHiddenOnMouseLeave) {
      this.popup.show(event);
    }
  }

  showSecEventsPopupOnMouseLeave(): void {
    if (this.popupHiddenOnMouseLeave) {
      this.popup.hide();
    }
  }
  generateVideoSectionStyle() {
    let result = {};
    const show = this.video.show;
    if (show && show.cssStyles != null && typeof show.cssStyles === "object") {
      result = Object.assign(result, show.cssStyles);
    }
    return result;
  }
}
